import React, { useEffect, useState } from 'react'
import { toastError, toastSuccess, toastLoading, toastDismiss } from '../utils/toastHelper'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import apiClient from '../services/apiClient'

import { formatDateDisplay, parseLocalDateString } from '../utils/dateHelpers'
import { formatCurrency } from '../utils/currencyHelpers'
import SimpleHeader from '../components/common/SimpleHeader'
import Footer from '../components/common/Footer'
import CountdownTimer from '../components/common/CountdownTimer'
import LoadingSpinner from '../components/common/LoadingSpinner'
import PaymentMethodSelector from '../components/payment/PaymentMethodSelector'
import TermsAndConditionsModal from '../components/checkout/TermsAndConditionsModal'
import { usePaymentMethods } from '../hooks/usePaymentMethods'
import { useDisponibilidadCheckout } from '../hooks/useEspacios'
import { useTheme } from '../hooks/useTheme'

interface GuestFormData {
  nombre: string
  email: string
  telefono: string
  numero_documento: string
  tipo_documento: string
  solicitudes_especiales: string
}

interface DesgloseItem {
  fecha: string
  dia_semana: string
  tarifa: number
}

interface EspacioInfo {
  id: string
  nombre: string
  codigo: string
  tarifa_base?: number
  unidad_reserva?: string
}

interface TarifaEspacioMultiple {
  espacio_id: string
  nombre: string
  codigo: string
  unidad_reserva: string
  cantidad_unidades: number
  tarifa_aplicada_por_unidad: number
  monto_total: number
}

interface ConsolidadoMultiples {
  unidad_reserva_comun: string
  dias_disponibles_comun: string[]
  monto_total: number
  valida: boolean
  razon_invalida?: string
  tarifas_por_espacio?: TarifaEspacioMultiple[]
}

interface BookingLockData {
  booking_token: string
  expires_at: string
  espacio?: {
    id: number | string
    nombre: string
    tarifa_simple?: number
    unidad_reserva?: string
  }
  // Para múltiples espacios
  espacios?: EspacioInfo[]
  consolidado?: ConsolidadoMultiples
  fecha_entrada: string
  fecha_salida: string
  cantidad_huespedes?: number  // Opcional, no se usa
  monto_total: number
  unidad_reserva?: string
  // ✅ Nueva información de tarifas desde backend
  tarifa_aplicada_por_unidad?: number
  tipo_tarifa?: string  // 'simple', 'múltiple', 'diferenciada_dias_semana'
  cantidad_unidades?: number
  desglose?: DesgloseItem[]  // Desglose por día (solo para diferenciada)
}

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { enabledMethods, loading: paymentMethodsLoading } = usePaymentMethods()
  const { colors } = useTheme()

  const [bookingData, setBookingData] = useState<BookingLockData | null>(null)
  const [reservaId, setReservaId] = useState<string | null>(null)  // Guardar reserva_id para poder cancelar
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [metodosPago, setMetodosPago] = useState<'getnet' | 'khipu' | 'transbank'>('getnet')
  const [cartItems, setCartItems] = useState<Array<{nombre: string; fecha_entrada: string; fecha_salida: string; monto_total: number}> | null>(null)
  const [availabilityChecked, setAvailabilityChecked] = useState(false)
  const [availabilityError, setAvailabilityError] = useState<string | null>(null)

  const [formData, setFormData] = useState<GuestFormData>({
    nombre: '',
    email: '',
    telefono: '',
    numero_documento: '',
    tipo_documento: 'RUT',
    solicitudes_especiales: ''
  })

  const [errors, setErrors] = useState<Partial<GuestFormData>>({})
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelingBooking, setCancelingBooking] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [showTermsModal, setShowTermsModal] = useState(false)

  useEffect(() => {
    // Obtener datos del bloqueo desde location.state o verificar con token
    const stateData = location.state as any | undefined
    const tokenFromUrl = searchParams.get('token')

    if (stateData) {
      // Si viene de ShoppingCart, tiene bookingData, clientData y cartItems
      if (stateData.bookingData) {
        setBookingData(stateData.bookingData)

        // Pre-llenar datos del cliente si vienen de ShoppingCart
        if (stateData.clientData) {
          setFormData(stateData.clientData)
        }

        // Guardar items del carrito si vienen de ShoppingCart
        if (stateData.cartItems) {
          setCartItems(stateData.cartItems)
        }

        // Establecer método de pago si viene de ShoppingCart
        if (stateData.paymentMethod) {
          setMetodosPago(stateData.paymentMethod)
        }
      } else {
        // Flujo V1: stateData es directamente BookingLockData
        setBookingData(stateData)
      }
    } else if (tokenFromUrl) {
      // Verificar el bloqueo con el token
      verifyBookingLock(tokenFromUrl)
    } else {
      toastError('No se encontró información de la reserva')
      navigate('/')
    }
  }, [location.state, searchParams])

  // Actualizar método de pago predeterminado cuando se carguen los métodos habilitados
  useEffect(() => {
    if (!paymentMethodsLoading && enabledMethods.length > 0) {
      const firstEnabledMethod = enabledMethods[0].id as 'getnet' | 'khipu' | 'transbank'
      setMetodosPago(firstEnabledMethod)
    }
  }, [paymentMethodsLoading, enabledMethods])

  const verifyBookingLock = async (token: string) => {
    setLoading(true)
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/espacios/verificar-bloqueo?booking_token=${token}`
      )

      if (!response.ok) {
        throw new Error('Bloqueo expirado o inválido')
      }

      const result = await response.json()
      setBookingData(result.data || result)
    } catch (error) {
      console.error('Error verificando bloqueo:', error)
      toastError('El bloqueo de tu reserva ha expirado. Por favor intenta nuevamente.')
      setTimeout(() => navigate('/'), 2000)
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmCancel = async () => {
    setCancelingBooking(true)
    if (!bookingData) {
      return
    }

    try {
      // 1. Cancelar el bloqueo temporal
      const bloqueoResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/api/espacios/cancelar-bloqueo/${bookingData.booking_token}`,
        {
          method: 'DELETE'
        }
      )
      if (!bloqueoResponse.ok) {
        console.warn('No se pudo cancelar el bloqueo (puede ya estar expirado)')
      }

      // 2. Si existe reserva_id, cancelar la reserva también
      if (reservaId) {
        try {
          const reservaResponse = await fetch(
            `${import.meta.env.VITE_API_URL}/api/espacios/cancelar-reserva/${reservaId}`,
            {
              method: 'DELETE'
            }
          )
          if (!reservaResponse.ok) {
            console.warn('No se pudo cancelar la reserva')
          }
        } catch (error) {
          console.error('Error al cancelar reserva:', error)
        }
      }

      toastError('Reserva cancelada. El espacio ha sido liberado.')
      setShowCancelModal(false)
      setTimeout(() => {
        navigate('/reservar')
      }, 2000)
    } catch (error) {
      console.error('Error al cancelar bloqueo:', error)
      toastError('Error al cancelar la reserva')
      setCancelingBooking(false)
    }
  }

  const cancelarBloqueoYVolver = async (mensaje: string) => {
    if (!bookingData) {
      return
    }

    try {
      // 1. Cancelar el bloqueo temporal
      const bloqueoResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/api/espacios/cancelar-bloqueo/${bookingData.booking_token}`,
        {
          method: 'DELETE'
        }
      )
      if (!bloqueoResponse.ok) {
        console.warn('No se pudo cancelar el bloqueo (puede ya estar expirado)')
      }

      // 2. Si existe reserva_id, cancelar la reserva también
      if (reservaId) {
        try {
          const reservaResponse = await fetch(
            `${import.meta.env.VITE_API_URL}/api/espacios/cancelar-reserva/${reservaId}`,
            {
              method: 'DELETE'
            }
          )
          if (!reservaResponse.ok) {
            console.warn('No se pudo cancelar la reserva')
          }
        } catch (error) {
          console.error('Error al cancelar reserva:', error)
        }
      }
    } catch (error) {
      console.error('Error al cancelar bloqueo:', error)
    } finally {
      // Navegar a la página de búsqueda independientemente del resultado
      toastError(mensaje)
      setTimeout(() => {
        navigate('/reservar')
      }, 2000)
    }
  }

  const handleExpire = () => {
    cancelarBloqueoYVolver('¡Tiempo expirado! Tu reserva ha sido liberada.')
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<GuestFormData> = {}

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre completo es requerido'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido'
    }
    if (!formData.telefono.trim()) {
      newErrors.telefono = 'El teléfono es requerido'
    }
    if (!formData.numero_documento.trim()) {
      newErrors.numero_documento = 'El número de documento es requerido'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // ⚠️ CRÍTICO: Prevenir múltiples clics usando flag síncrona ANTES de setSubmitting
    // React batches state updates, permitiendo múltiples clics antes de que `submitting` se actualice
    if (submitting) {
      return
    }

    if (!validateForm() || !bookingData) {
      return
    }

    setSubmitting(true)

    try {
      // ✅ FASE 5: Validación final con rev_disponibilidad_checkout ANTES de completar reserva
      // Esto valida que los espacios sigan disponibles (último chequeo antes de pagar)

      let espaciosAValidar: Array<{espacio_id: string; fecha_entrada: string; fecha_salida: string; cantidad_unidades: number}> = []

      // ✅ Usar cartItems si existen (flujo V2 - carrito), o bookingData (flujo V1)
      if (cartItems && cartItems.length > 0) {
        // Modo múltiples espacios (flujo V2 - carrito)
        // Obtener IDs de espacios desde consolidado.tarifas_por_espacio
        if (bookingData.consolidado?.tarifas_por_espacio) {
          espaciosAValidar = bookingData.consolidado.tarifas_por_espacio.map(espacio => {
            // Buscar el item del carrito para obtener las fechas correctas
            const cartItem = cartItems.find(item => item.nombre.includes(espacio.nombre))
            const fechaEntrada = cartItem?.fecha_entrada || bookingData.fecha_entrada
            const fechaSalida = cartItem?.fecha_salida || bookingData.fecha_salida

            if (!fechaEntrada || !fechaSalida) {
              throw new Error('No se encontraron fechas de entrada y salida')
            }

            const entrada = parseLocalDateString(fechaEntrada)
            const salida = parseLocalDateString(fechaSalida)
            const diasDiferencia = Math.floor((salida.getTime() - entrada.getTime()) / (1000 * 60 * 60 * 24))
            const cantidad_unidades = espacio.unidad_reserva === 'dia' ? diasDiferencia + 1 : diasDiferencia

            return {
              espacio_id: espacio.espacio_id,
              fecha_entrada: fechaEntrada,
              fecha_salida: fechaSalida,
              cantidad_unidades
            }
          })
        }
      } else {
        // Modo un solo espacio (flujo V1)
        if (bookingData.fecha_entrada && bookingData.fecha_salida) {
          const entrada = parseLocalDateString(bookingData.fecha_entrada)
          const salida = parseLocalDateString(bookingData.fecha_salida)
          const diasDiferencia = Math.floor((salida.getTime() - entrada.getTime()) / (1000 * 60 * 60 * 24))
          const unidadReserva = bookingData.unidad_reserva || 'noche'
          const cantidad_unidades = unidadReserva === 'dia' ? diasDiferencia + 1 : diasDiferencia

          if (bookingData.espacio_id) {
            espaciosAValidar = [{
              espacio_id: bookingData.espacio_id.toString(),
              fecha_entrada: bookingData.fecha_entrada,
              fecha_salida: bookingData.fecha_salida,
              cantidad_unidades
            }]
          }
        }
      }

      // Validar disponibilidad final para cada espacio
      for (const espacio of espaciosAValidar) {
        // Construir query parameters
        const params = new URLSearchParams({
          fecha_entrada: espacio.fecha_entrada,
          fecha_salida: espacio.fecha_salida,
          cantidad_unidades: espacio.cantidad_unidades.toString(),
          bloqueo_temporal_id: bookingData.booking_token  // Pasar token como ID del bloqueo
        })

        const validacionCheckoutResponse = await fetch(
          `${import.meta.env.VITE_API_URL}/api/espacios/rev_disponibilidad_checkout/${espacio.espacio_id}?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )

        if (!validacionCheckoutResponse.ok) {
          const errorData = await validacionCheckoutResponse.json()

          // Cancelar bloqueo si hay error de disponibilidad
          await cancelarBloqueoYVolver(
            errorData.detail ||
            'El espacio ya no está disponible en las fechas seleccionadas. Por favor intenta con otras fechas.'
          )
          return
        }
      }

      // ✅ Todas las validaciones pasaron - proceder con completar reserva
      // 1. Completar la reserva con el token
      // ✅ Usar apiClient en lugar de fetch para timeout consistente y sin reintentos múltiples
      const completarResponse = await apiClient.post(
        '/api/espacios/completar-reserva',
        {
          token: bookingData.booking_token,
          nombre: formData.nombre,
          email: formData.email,
          telefono: formData.telefono,
          numero_documento: formData.numero_documento,
          tipo_documento: formData.tipo_documento,
          solicitudes_especiales: formData.solicitudes_especiales || null
        }
      )

      const reservaData = completarResponse.data

      const nuevaReservaId = reservaData?.reserva_id

      if (!nuevaReservaId) {
        throw new Error(`No se pudo obtener reserva_id. ReservaData: ${JSON.stringify(reservaData)}`)
      }

      // Guardar reserva_id en state para poder cancelarla si falla el pago
      setReservaId(nuevaReservaId)

      toastSuccess('Reserva creada exitosamente')

      // 2. Crear sesión de pago con el método seleccionado

      const paymentEndpoint =
        metodosPago === 'khipu'
          ? '/api/payments/khipu/create-payment'
          : metodosPago === 'transbank'
          ? '/api/payments/transbank/create-transaction'
          : '/api/payments/getnet/create-session'

      const paymentPayload =
        metodosPago === 'khipu'
          ? {
              reserva_id: nuevaReservaId,
              payer_email: formData.email,
              payer_name: formData.nombre
            }
          : metodosPago === 'transbank'
          ? {
              reserva_id: nuevaReservaId
            }
            : {
                reserva_id: nuevaReservaId,
                buyer_email: formData.email,
                buyer_name: formData.nombre,
                buyer_document: formData.numero_documento
              }

      // ✅ Usar apiClient en lugar de fetch (incluye token de autenticación)
      // apiClient retorna el objeto completo de axios, el response está en .data
      const paymentResponse = await apiClient.post(paymentEndpoint, paymentPayload)

      const paymentData = paymentResponse.data

      if (metodosPago === 'khipu') {
        if (!paymentData?.payment_url) {
          throw new Error(`Respuesta inválida de Khipu: ${JSON.stringify(paymentData)}`)
        }

        // Guardar información en localStorage para Khipu
        localStorage.setItem('payment_method', 'khipu')
        localStorage.setItem('khipu_payment_id', paymentData.payment_id)
        localStorage.setItem('payment_reserva_id', nuevaReservaId.toString())

        // Redirigir a Khipu
        toastLoading('Redirigiendo a Khipu...', { duration: 1000 })
        setTimeout(() => {
          window.location.href = paymentData.payment_url
        }, 1000)
      } else if (metodosPago === 'transbank') {
        // Transbank
        if (!paymentData?.token || !paymentData?.url) {
          throw new Error(`Respuesta inválida de Transbank: ${JSON.stringify(paymentData)}`)
        }

        // Guardar información en localStorage para Transbank
        localStorage.setItem('payment_method', 'transbank')
        localStorage.setItem('transbank_token', paymentData.token)
        localStorage.setItem('payment_reserva_id', nuevaReservaId.toString())

        // Redirigir a Transbank con token como parámetro
        toastLoading('Redirigiendo a Transbank...', { duration: 1000 })
        setTimeout(() => {
          const transbankUrl = `${paymentData.url}?token_ws=${paymentData.token}`
          window.location.href = transbankUrl
        }, 1000)
      } else {
        // Getnet
        if (!paymentData?.requestId) {
          throw new Error(`Respuesta inválida de Getnet: ${JSON.stringify(paymentData)}`)
        }

        // Guardar información en localStorage para Getnet
        localStorage.setItem('payment_method', 'getnet')
        localStorage.setItem('payment_request_id', paymentData.requestId.toString())
        localStorage.setItem('payment_reserva_id', nuevaReservaId.toString())

        // Redirigir a Getnet
        toastLoading('Redirigiendo a Getnet...', { duration: 1000 })
        setTimeout(() => {
          window.location.href = paymentData.processUrl
        }, 1000)
      }

    } catch (error) {
      toastError(error instanceof Error ? error.message : 'Error al procesar el pago')
      setSubmitting(false)
    }
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    // Limpiar error del campo al escribir
    if (errors[name as keyof GuestFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Cargando información...</p>
        </div>
      </div>
    )
  }

  if (!bookingData) {
    return null
  }

  // Calcular cantidad de unidades usando fechas locales, no UTC
  const unidad_reserva = bookingData.consolidado?.unidad_reserva_comun || bookingData.unidad_reserva || 'noche'

  // Validar que las fechas existan antes de calcular
  const fechaEntrada = bookingData.fecha_entrada
  const fechaSalida = bookingData.fecha_salida

  let cantidad_unidades = 0
  if (fechaEntrada && fechaSalida) {
    const dias_diferencia = Math.floor(
      (parseLocalDateString(fechaSalida).getTime() -
       parseLocalDateString(fechaEntrada).getTime()) /
      (1000 * 60 * 60 * 24)
    )
    cantidad_unidades = unidad_reserva === 'dia' ? dias_diferencia + 1 : dias_diferencia
  }

  const obtenerEtiquetaUnidad = (): string => {
    if (unidad_reserva === 'dia') {
      return `${cantidad_unidades} día${cantidad_unidades !== 1 ? 's' : ''}`
    }
    return `${cantidad_unidades} noche${cantidad_unidades !== 1 ? 's' : ''}`
  }

  const obtenerEtiquetaTarifa = (capitalizar: boolean = false, pluralizar: boolean = false): string => {
    const base = capitalizar
      ? (unidad_reserva === 'dia' ? 'Día' : 'Noche')
      : (unidad_reserva === 'dia' ? 'día' : 'noche')
    return pluralizar ? base + 's' : base
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SimpleHeader
        title="Completar Reserva"
        subtitle="Ingresa tus datos para finalizar la reserva"
      />

      <div className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Countdown Timer */}
          <CountdownTimer
            expiresAt={bookingData.expires_at}
            onExpire={handleExpire}
            className="mb-6"
          />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Formulario de huésped */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow-xl rounded-2xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Datos del Cliente
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nombre Completo */}
                <div>
                  <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre Completo *
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    placeholder="Ej: Juan Carlos Pérez"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.nombre ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={submitting}
                  />
                  {errors.nombre && (
                    <p className="mt-1 text-sm text-red-600">{errors.nombre}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={submitting}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                {/* Teléfono */}
                <div>
                  <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    placeholder="+56 9 1234 5678"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.telefono ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={submitting}
                  />
                  {errors.telefono && (
                    <p className="mt-1 text-sm text-red-600">{errors.telefono}</p>
                  )}
                </div>

                {/* Tipo y Número de Documento */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="tipo_documento" className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo Doc *
                    </label>
                    <select
                      id="tipo_documento"
                      name="tipo_documento"
                      value={formData.tipo_documento}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      disabled={submitting}
                    >
                      <option value="RUT">RUT</option>
                      <option value="DNI">DNI</option>
                      <option value="Pasaporte">Pasaporte</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="numero_documento" className="block text-sm font-medium text-gray-700 mb-1">
                      Número de Documento *
                    </label>
                    <input
                      type="text"
                      id="numero_documento"
                      name="numero_documento"
                      value={formData.numero_documento}
                      onChange={handleInputChange}
                      placeholder="12.345.678-9"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                        errors.numero_documento ? 'border-red-500' : 'border-gray-300'
                      }`}
                      disabled={submitting}
                    />
                    {errors.numero_documento && (
                      <p className="mt-1 text-sm text-red-600">{errors.numero_documento}</p>
                    )}
                  </div>
                </div>

                {/* Solicitudes Especiales */}
                <div>
                  <label htmlFor="solicitudes_especiales" className="block text-sm font-medium text-gray-700 mb-1">
                    Solicitudes Especiales (Opcional)
                  </label>
                  <textarea
                    id="solicitudes_especiales"
                    name="solicitudes_especiales"
                    value={formData.solicitudes_especiales}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="Ej: Llegada tardía, necesidades especiales, etc."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                    disabled={submitting}
                  />
                </div>

                {/* Método de Pago - Componente mejorado */}
                <PaymentMethodSelector
                  selectedMethod={metodosPago}
                  onMethodChange={(method) => setMetodosPago(method)}
                  disabled={submitting}
                />

                {/* Checkbox de Términos y Condiciones */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      disabled={submitting}
                      className="w-5 h-5 mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                    <div className="flex-1">
                      <p className="text-sm text-gray-700">
                        He leído y acepto los{' '}
                        <button
                          type="button"
                          onClick={() => setShowTermsModal(true)}
                          className="font-semibold transition-colors"
                          style={{ color: colors.primary }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.textDecoration = 'underline'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.textDecoration = 'none'
                          }}
                        >
                          términos y condiciones
                        </button>{' '}
                        del Camping Playa Niklitschek
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Incluye el reglamento general que será enviado a tu correo
                      </p>
                    </div>
                  </label>
                </div>

                {/* Botón de submit */}
                {(() => {
                  const formDataCompleto = formData.nombre && formData.email && formData.telefono && formData.numero_documento
                  const tieneItems = cartItems && cartItems.length > 0
                  const puedeSubmit = formDataCompleto && termsAccepted && tieneItems

                  return (
                    <button
                      type="submit"
                      disabled={submitting || !puedeSubmit}
                      className="w-full py-4 px-6 rounded-xl font-semibold text-white text-lg flex items-center justify-center transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: puedeSubmit ? colors.primary : '#9ca3af',
                        cursor: puedeSubmit ? 'pointer' : 'not-allowed'
                      }}
                      onMouseEnter={(e) => {
                        if (puedeSubmit && !submitting) {
                          e.currentTarget.style.backgroundColor = colors.primaryDark
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (puedeSubmit && !submitting) {
                          e.currentTarget.style.backgroundColor = colors.primary
                        }
                      }}
                    >
                      {submitting ? (
                        <>
                          <LoadingSpinner size="sm" />
                          <span className="ml-2">Procesando...</span>
                        </>
                      ) : !formDataCompleto ? (
                        '✓ Completa los datos del cliente'
                      ) : !termsAccepted ? (
                        '✓ Acepta los términos para continuar'
                      ) : !tieneItems ? (
                        '✓ El carrito está vacío'
                      ) : (
                        '💳 Continuar al Pago'
                      )}
                    </button>
                  )
                })()}
              </form>
            </div>
          </div>

          {/* Resumen de la reserva */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow-xl rounded-2xl p-6 sticky top-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Resumen de Reserva
              </h3>

              <div className="space-y-3">
                {/* Items del carrito V2 (si vienen de ShoppingCart) */}
                {cartItems && cartItems.length > 0 && (
                  <div className="pb-3 border-b border-gray-200">
                    <p className="text-sm text-gray-600 mb-3 font-semibold">Espacios Reservados</p>
                    <div className="space-y-2">
                      {cartItems.map((item, index) => (
                        <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900 text-sm">{item.nombre}</p>
                              <p className="text-xs text-gray-600 mt-1">
                                {item.fecha_entrada} → {item.fecha_salida}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold" style={{ color: colors.primary }}>
                                {formatCurrency(item.monto_total)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Si NO vienen del carrito V2, mostrar la información detallada */}
                {!cartItems && (
                  <>
                    {/* Mostrar espacios - múltiples o único */}
                    {bookingData.espacios && bookingData.espacios.length > 0 ? (
                      <div className="pb-3 border-b border-gray-200">
                        <p className="text-sm text-gray-600 mb-3 font-semibold">Desglose por Espacio</p>
                        <div className="space-y-2">
                          {bookingData.consolidado?.tarifas_por_espacio && bookingData.consolidado.tarifas_por_espacio.length > 0 ? (
                            // Mostrar desglose detallado con información de tarifas
                            bookingData.consolidado.tarifas_por_espacio.map((tarifa) => (
                              <div key={tarifa.espacio_id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex justify-between items-start mb-2">
                                  <div>
                                    <p className="font-semibold text-gray-900">{tarifa.nombre}</p>
                                    <p className="text-xs text-gray-600">{tarifa.codigo}</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm font-bold text-green-600">
                                      {formatCurrency(tarifa.monto_total)}
                                    </p>
                                    <p className="text-xs text-gray-600">
                                      {tarifa.cantidad_unidades} {tarifa.unidad_reserva === 'dia' ? 'día' : 'noche'}{tarifa.cantidad_unidades !== 1 ? 's' : ''}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex justify-between text-xs text-gray-700 bg-white rounded px-2 py-1">
                                  <span>{formatCurrency(tarifa.tarifa_aplicada_por_unidad)} × {tarifa.cantidad_unidades}</span>
                                  <span className="font-semibold">{formatCurrency(tarifa.monto_total)}</span>
                                </div>
                              </div>
                            ))
                          ) : (
                            // Fallback: mostrar solo nombres si no tenemos desglose detallado
                            bookingData.espacios.map(espacio => (
                              <div key={espacio.id} className="p-2 bg-camping-50 rounded border border-camping-200">
                                <p className="font-semibold text-gray-900">{espacio.nombre}</p>
                                <p className="text-xs text-gray-600">{espacio.codigo}</p>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="pb-3 border-b border-gray-200">
                        <p className="text-sm text-gray-600">Espacio</p>
                        <p className="font-semibold text-gray-900">{bookingData.espacio?.nombre}</p>
                      </div>
                    )}

                    {fechaEntrada && (
                      <div className="pb-3 border-b border-gray-200">
                        <p className="text-sm text-gray-600">Entrada</p>
                        <p className="font-semibold text-gray-900">
                          {formatDateDisplay(fechaEntrada)}
                        </p>
                      </div>
                    )}

                    {fechaSalida && (
                      <div className="pb-3 border-b border-gray-200">
                        <p className="text-sm text-gray-600">Salida</p>
                        <p className="font-semibold text-gray-900">
                          {formatDateDisplay(fechaSalida)}
                        </p>
                      </div>
                    )}

                    <div className="pb-3 border-b border-gray-200">
                      <p className="text-sm text-gray-600">{obtenerEtiquetaTarifa(true, true)}</p>
                      <p className="font-semibold text-gray-900">{cantidad_unidades}</p>
                    </div>

                    {/* ✅ Desglose de tarifa - Varía según tipo - Solo para single space */}
                    {!bookingData.espacios && (
                      <>
                        <div className="pb-3 border-b border-gray-200">
                          <p className="text-sm text-gray-600">Tarifa Aplicada</p>
                          <div className="space-y-1">
                            {bookingData.tipo_tarifa === 'diferenciada_dias_semana' && bookingData.desglose ? (
                              <>
                                <p className="text-xs text-gray-600 uppercase tracking-wide font-semibold">
                                  📅 Resumen de Tarifas
                                </p>
                                <div className="space-y-2 mt-2">
                                  {(() => {
                                    // Agrupar por tarifa
                                    const agrupadoPorTarifa: { [key: number]: { cantidad: number; tarifa: number; ejemploDia: string } } = {}

                                    bookingData.desglose!.forEach((dia) => {
                                      const tarifa = dia.tarifa
                                      if (!agrupadoPorTarifa[tarifa]) {
                                        agrupadoPorTarifa[tarifa] = { cantidad: 0, tarifa, ejemploDia: dia.dia_semana }
                                      }
                                      agrupadoPorTarifa[tarifa].cantidad += 1
                                    })

                                    return Object.values(agrupadoPorTarifa).map((grupo) => (
                                      <div key={grupo.tarifa} className="flex justify-between text-xs text-gray-700">
                                        <span className="font-semibold">
                                          {grupo.cantidad} {grupo.cantidad === 1 ? 'día' : 'días'} × {formatCurrency(grupo.tarifa)}
                                        </span>
                                        <span className="font-semibold">
                                          {formatCurrency(grupo.cantidad * grupo.tarifa)}
                                        </span>
                                      </div>
                                    ))
                                  })()}
                                </div>
                              </>
                            ) : (
                              <>
                                <p className="font-semibold text-gray-900">
                                  {formatCurrency(bookingData.tarifa_aplicada_por_unidad ?? 0)}
                                  <span className="text-xs text-gray-500 ml-1">
                                    / {obtenerEtiquetaTarifa()}
                                  </span>
                                </p>
                                <p className="text-xs text-gray-600 uppercase tracking-wide">
                                  {bookingData.tipo_tarifa === 'múltiple' && '💰 Tarifa Múltiple'}
                                  {bookingData.tipo_tarifa === 'simple' && '💰 Tarifa Simple'}
                                </p>
                              </>
                            )}
                          </div>
                        </div>

                        {bookingData.tipo_tarifa !== 'diferenciada_dias_semana' && (
                          <div className="pb-3 border-b border-gray-200">
                            <p className="text-xs text-gray-500">
                              {cantidad_unidades} × {formatCurrency(bookingData.tarifa_aplicada_por_unidad ?? 0)}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                <div className="pt-3">
                  <div className="flex justify-between items-center">
                    <p className="text-lg font-bold text-gray-900">
                      Total a Pagar {cartItems && cartItems.length > 1 && '(Todos los espacios)'}
                    </p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(
                        cartItems
                          ? cartItems.reduce((sum, item) => sum + item.monto_total, 0)
                          : (bookingData.consolidado?.monto_total || bookingData.monto_total)
                      )}
                    </p>
                  </div>
                  {cartItems && cartItems.length > 1 && (
                    <p className="text-xs text-gray-600 mt-2">
                      {cartItems.length} espacios
                    </p>
                  )}
                </div>
              </div>

              {/* Información de seguridad */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-start space-x-2">
                  <svg className="w-5 h-5 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <div>
                    <p className="text-xs font-semibold text-gray-900">Pago Seguro</p>
                    <p className="text-xs text-gray-600">
                      Tu información está protegida con encriptación SSL
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

          {/* Botón de cancelar */}
          <div className="mt-6 text-center">
            <button
              onClick={() => setShowCancelModal(true)}
              disabled={submitting}
              className="px-6 py-2 text-sm font-semibold rounded-lg border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                borderColor: colors.primary,
                color: colors.primary,
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => {
                if (!submitting) {
                  e.currentTarget.style.backgroundColor = `${colors.primary}10`
                }
              }}
              onMouseLeave={(e) => {
                if (!submitting) {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }
              }}
            >
              ❌ Cancelar reserva
            </button>
          </div>
        </div>
      </div>

      {/* ✅ Modal de confirmación personalizado */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <div className="text-center space-y-4">
              <div className="text-5xl mb-2">⚠️</div>
              <h3 className="text-2xl font-bold text-gray-900">
                Cancelar Reserva
              </h3>
              <p className="text-gray-600">
                ¿Estás seguro de que deseas cancelar esta reserva? El espacio será liberado inmediatamente.
              </p>
            </div>

            <div className="mt-8 flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={cancelingBooking}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Mantener Reserva
              </button>
              <button
                onClick={handleConfirmCancel}
                disabled={cancelingBooking}
                className="flex-1 px-6 py-3 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                style={{
                  backgroundColor: colors.primaryDark
                }}
                onMouseEnter={(e) => {
                  if (!cancelingBooking) {
                    e.currentTarget.style.opacity = '0.9'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!cancelingBooking) {
                    e.currentTarget.style.opacity = '1'
                  }
                }}
              >
                {cancelingBooking ? 'Cancelando...' : 'Sí, Cancelar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Términos y Condiciones */}
      <TermsAndConditionsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />

      <Footer />
    </div>
  )
}

export default CheckoutPage
